import Card from 'components/Layout/Card'
import PageHeader from 'components/Layout/PageHeader'
import Pagination from 'components/Pagination'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './Broadsign.scss'
import Input from 'components/Form/Input'
import Select, { SelectOption } from 'components/Form/Select'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import TsxUtils from '../../utils/tsx'
import FillingSpinner, { FillingSpinnerType } from '../../components/FillingSpinner'
import BroadSignCreationMediaTable from './components/BroadSignCreationMediaTable/BroadSignCreationMediaTable'
import debounce from 'lodash/debounce'
import BroadSignService from './services/broad-sign.service'
import BroadSignCreationModal from './containers/BroadSignCreationModal'
import { broadSignActions } from './store/broad-sign-slice'
import { BroadSignCreationModel } from '../../api/broadSign/models/broad-sign-creation.model'
import { BroadSignFiltersModel } from '../../api/broadSign/models/broad-sign-filters.model'
import DateUtils from '../../utils/date'
import { BROADSIGN_CREATIONS_PER_PAGE, DATE_FORMAT, DATE_HOUR_FORMAT } from '../../constant'
import { MediaFormat } from '../../types/campaign'
import IconAlert from '../../components/Icon/IconAlert'
import Tooltip from '../../components/Tooltip'
import Button, { ButtonSize, ButtonTheme } from '../../components/Form/Button'
import { AppContext } from '../../contexts/AppContext'
import BroadSignRejectCreationModal from './containers/BroadSignRejectCreationModal/BroadSignRejectCreationModal'
import { faCirclePlay, faSpinner } from '@fortawesome/free-solid-svg-icons'
import SelectAsync from '../../components/Form/SelectAsync'
import BasicsFormService from '../Campaign/CampaignForm/BasicsForm/services/basics-form.service'
import BroadSignCreationPreviewModal from './components/BroadSignCreationPreviewModal/BroadSignCreationPreviewModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// the two statuses a decision leaves behind, the only ones the preview marks out. everything else
// is still waiting and reads as plain text
const DECIDED_STATUSES = ['accepted', 'rejected']

const creationTitle = (creation: BroadSignCreationModel): string =>
  creation.details || creation.file.filename

const mediaFormatLabelKey = (mediaFormat: string): string =>
  `mediaType.${MediaFormat[mediaFormat as keyof typeof MediaFormat]}`

const Broadsign: React.FC = () => {
  const debounceTime = 2000
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { addNotification } = useContext(AppContext)
  const {
    loading,
    acceptancesLoading,
    pageNumber,
    totalItemsCount,
    data,
    acceptances,
    dropdowns,
    filters,
    selectedCreation,
  } = useSelector((state: RootState) => state.broadSign)

  // the creation on screen is held whole rather than looked up in the list: the list is sorted on
  // the not-sent count that sending the buildings takes down to zero, so a reload can carry the
  // creation off the page it was picked from and it must stay picked through that
  const [previewedCreation, setPreviewedCreation] = useState<BroadSignCreationModel | null>(null)
  const [hoveredAlertCreationId, setHoveredAlertCreationId] = useState<string | null>(null)
  const [showRejectionModal, setShowRejectionModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [saving, setSaving] = useState(false)

  const previewedCreationId = previewedCreation?.id ?? null

  const mediaFormatOptions = dropdowns.mediaFormats.map((mediaFormat): SelectOption => {
    return {
      value: mediaFormat,
      label: t(mediaFormatLabelKey(mediaFormat)),
    }
  })

  const updateFilters = (changedFilters: Partial<BroadSignFiltersModel>) => {
    void dispatch(broadSignActions.setFilters({ ...filters, ...changedFilters }))
  }

  const debouncedFetchBroadSignCreations = useCallback(
    debounce(BroadSignService.fetchBroadSignCreations, debounceTime),
    []
  )

  useEffect(() => {
    const firstPage = 1

    // the fetch itself is debounced, so flag loading up front to cover the waiting time
    void dispatch(broadSignActions.setLoading(true))
    setPreviewedCreation(null)
    void debouncedFetchBroadSignCreations(filters, firstPage)
  }, [filters])

  // a reloaded list refreshes the creation on screen, and leaves it as it stands when the reload
  // put it on another page
  useEffect(() => {
    setPreviewedCreation(
      current => (current && data.find(creation => creation.id === current.id)) || current
    )
  }, [data])

  useEffect(() => {
    // the rejection reason and the media popup belong to the creation they were opened on, another
    // one starts over
    setShowRejectionModal(false)
    setShowPreviewModal(false)

    if (!previewedCreationId) {
      void dispatch(broadSignActions.setAcceptances([]))

      return
    }

    void BroadSignService.fetchCreationAcceptances(previewedCreationId)
  }, [previewedCreationId])

  useEffect(() => {
    return () => {
      dispatch(broadSignActions.resetState())
    }
  }, [])

  const setPageNumber = (page: number) => {
    void BroadSignService.fetchBroadSignCreations(filters, page)
  }

  const closeCreationModal = () => {
    void dispatch(broadSignActions.setSelectedCreation(undefined))
  }

  // the status is taken straight onto the creation on screen as well as into the list: a reload can
  // have carried it off the loaded page, and then the list has nothing to hand it back
  const setPreviewedCreationStatus = (status: string) => {
    setPreviewedCreation(current => (current ? { ...current, status } : current))
  }

  const acceptPreviewedCreation = () => {
    if (!previewedCreation) {
      return
    }

    setAccepting(true)

    void BroadSignService.acceptCreation(
      previewedCreation.id,
      addNotification,
      t,
      setPreviewedCreationStatus
    ).finally(() => setAccepting(false))
  }

  const updatePreviewedCreation = (changes: Partial<BroadSignCreationModel>) => {
    setPreviewedCreation(current => (current ? { ...current, ...changes } : current))
  }

  const savePreviewedCreation = () => {
    if (!previewedCreation) {
      return
    }

    setSaving(true)

    void BroadSignService.updateBroadsignCreation(
      previewedCreation.id,
      addNotification,
      t,
      filters,
      pageNumber,
      previewedCreation.commercialSupervisor?.value,
      previewedCreation.department?.rawData?.id,
      previewedCreation.advertiser?.rawData?.id
    ).finally(() => setSaving(false))
  }

  return (
    <div className={'Broadsign__container'}>
      <PageHeader>{t('broadSign.acceptanceTitle')}</PageHeader>

      <div className='Broadsign__layout'>
        <div className='Broadsign__sidebar'>
          <Card className='Broadsign__filters'>
            <Input
              id='text'
              title={t('common.search')}
              value={filters.text}
              onChange={text => updateFilters({ text })}
              placeholder={t('broadSign.searchPlaceholder')}
              dataCy={'broadsign-search-input'}
            />
            <Select
              id='mediaFormats'
              title={t('broadSign.kind')}
              value={filters.mediaFormats[0] ?? null}
              options={mediaFormatOptions}
              onChange={val => updateFilters({ mediaFormats: val ? [val as string] : [] })}
              clearable
              dataCy={'broadsign-media-formats-select'}
            />
          </Card>

          <Card className='Broadsign__creations-list'>
            <div className='Broadsign__creations-list-title'>{t('broadSign.selectCreation')}</div>
            {loading ? (
              <FillingSpinner
                type={FillingSpinnerType.STANDARD}
                className='Broadsign__creations-list-spinner'
              />
            ) : (
              <>
                {!data.length && (
                  <div className='Broadsign__creations-list-no-data'>{t('common.noData')}</div>
                )}
                {data.length > 0 && (
                  <div className='Broadsign__creations-list-items'>
                    {data.map(creation => (
                      <div
                        key={creation.id}
                        className={
                          'Broadsign__creation-item' +
                          TsxUtils.extraStyle(
                            previewedCreationId === creation.id,
                            'Broadsign__creation-item--active'
                          )
                        }
                        onClick={() => setPreviewedCreation(creation)}
                      >
                        <div className='Broadsign__creation-item-header'>
                          <div className='Broadsign__creation-item-title'>
                            {creationTitle(creation)}
                          </div>
                          {creation.usedBuildingsNotSentCount > 0 && (
                            <div
                              className='Broadsign__creation-item-alert'
                              onMouseEnter={() => setHoveredAlertCreationId(creation.id)}
                              onMouseLeave={() => setHoveredAlertCreationId(null)}
                            >
                              <IconAlert />
                              {hoveredAlertCreationId === creation.id && (
                                <Tooltip>
                                  {t('broadSign.usedBuildingsNotSent', {
                                    number: creation.usedBuildingsNotSentCount,
                                  })}
                                </Tooltip>
                              )}
                            </div>
                          )}
                        </div>
                        <div className='Broadsign__creation-item-desc'>
                          {t('broadSign.id')}: {creation.broadsignId}
                        </div>
                        <div className='Broadsign__creation-item-desc'>
                          {t('broadSign.createdAt')}:{' '}
                          {DateUtils.parseAndFormat(creation.createdAt, DATE_FORMAT)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <Pagination
                  activePage={pageNumber}
                  itemsCountPerPage={BROADSIGN_CREATIONS_PER_PAGE}
                  totalItemsCount={totalItemsCount}
                  pageRangeDisplayed={3}
                  onChange={setPageNumber}
                />
              </>
            )}
          </Card>

          {previewedCreation && (
            <Card className='Broadsign__preview'>
              <div className='Broadsign__preview-title'>{t('broadSign.creationPreview')}</div>
              <div
                className='Broadsign__preview-image'
                onClick={() => setShowPreviewModal(true)}
              >
                <img
                  src={previewedCreation.thumbnailUrl}
                  alt={previewedCreation.file.filename}
                />
                {previewedCreation.file.isVideo && (
                  <FontAwesomeIcon
                    className='Broadsign__preview-play-icon'
                    icon={faCirclePlay}
                  />
                )}
              </div>
              <div className='Broadsign__preview-details'>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.name')}</span>
                  <span className='Broadsign__preview-value'>
                    {creationTitle(previewedCreation)}
                  </span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.resolution')}</span>
                  <span className='Broadsign__preview-value'>
                    {previewedCreation.width + 'x' + previewedCreation.height + 'px'}
                  </span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.id')}</span>
                  <span className='Broadsign__preview-value'>{previewedCreation.broadsignId}</span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.kind')}</span>
                  <span className='Broadsign__preview-value'>
                    {previewedCreation.mediaFormat
                      ? t(mediaFormatLabelKey(previewedCreation.mediaFormat))
                      : '-'}
                  </span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('broadSign.status')}</span>
                  <span
                    className={
                      'Broadsign__preview-value' +
                      TsxUtils.extraStyle(
                        DECIDED_STATUSES.includes(previewedCreation.status),
                        `Broadsign__preview-value--${previewedCreation.status}`
                      )
                    }
                  >
                    {t(`broadSign.${previewedCreation.status}`)}
                  </span>
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>
                    {t('broadSign.aiRecommendation')}
                  </span>
                  <span
                    className={
                      'Broadsign__preview-value' +
                      ` Broadsign__preview-value--ai-${previewedCreation.aiRecommendationStatus}`
                    }
                  >
                    {t(
                      `broadSign.aiRecommendationStatuses.${previewedCreation.aiRecommendationStatus}`,
                      previewedCreation.aiRecommendationStatus
                    )}
                  </span>
                </div>
                {/* set once the evaluation finishes, never for a skipped creation */}
                {previewedCreation.aiRecommendationProcessedAt && (
                  <div className='Broadsign__preview-row'>
                    <span className='Broadsign__preview-label'>
                      {t('broadSign.aiRecommendationProcessedAt')}
                    </span>
                    <span className='Broadsign__preview-value'>
                      {DateUtils.parseAndFormat(
                        previewedCreation.aiRecommendationProcessedAt,
                        DATE_HOUR_FORMAT
                      )}
                    </span>
                  </div>
                )}
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('common.advertiser')}</span>
                  <SelectAsync
                    id='advertiser'
                    placeholder={t('common.advertiser')}
                    value={previewedCreation.advertiser}
                    isClearable={true}
                    label={t('common.search') + '...'}
                    menuPortalTarget={document.body}
                    options={BasicsFormService.getCompanies}
                    onChange={advertiser => updatePreviewedCreation({ advertiser })}
                    asyncFiltering={true}
                    dataCy={'broadsign-preview-advertiser-select'}
                  />
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>
                    {t('common.commercialSupervisor')}
                  </span>
                  <SelectAsync
                    id='commercialSupervisor'
                    placeholder={t('common.commercialSupervisor')}
                    value={previewedCreation.commercialSupervisor}
                    isClearable={true}
                    label={t('common.search') + '...'}
                    menuPortalTarget={document.body}
                    options={BasicsFormService.getUsersForSupervisors}
                    onChange={commercialSupervisor =>
                      updatePreviewedCreation({ commercialSupervisor })
                    }
                    dataCy={'broadsign-preview-commercial-supervisor-select'}
                  />
                </div>
                <div className='Broadsign__preview-row'>
                  <span className='Broadsign__preview-label'>{t('common.department')}</span>
                  <SelectAsync
                    id='department'
                    placeholder={t('common.department')}
                    value={previewedCreation.department}
                    isClearable={true}
                    label={t('common.search') + '...'}
                    menuPortalTarget={document.body}
                    options={BasicsFormService.getDepartments}
                    onChange={department => updatePreviewedCreation({ department })}
                    asyncFiltering={true}
                    dataCy={'broadsign-preview-department-select'}
                  />
                </div>
              </div>
              <Button
                className='Broadsign__preview-save'
                theme={ButtonTheme.GREEN}
                size={ButtonSize.SMALL}
                icon={saving ? faSpinner : undefined}
                onClick={savePreviewedCreation}
                disabled={saving}
                dataCy={'broadsign-preview-save-button'}
              >
                {t('common.save')}
              </Button>
              <div className='Broadsign__preview-actions'>
                <Button
                  theme={ButtonTheme.BLUE}
                  size={ButtonSize.SMALL}
                  icon={accepting ? faSpinner : undefined}
                  onClick={acceptPreviewedCreation}
                  disabled={accepting || previewedCreation.status === 'accepted'}
                  dataCy={'broadsign-preview-accept-button'}
                >
                  {t('broadSign.approve')}
                </Button>
                <Button
                  theme={ButtonTheme.BLUE_OUTLINE}
                  size={ButtonSize.SMALL}
                  onClick={() => setShowRejectionModal(true)}
                  disabled={accepting || previewedCreation.status === 'rejected'}
                  dataCy={'broadsign-preview-reject-button'}
                >
                  {t('broadSign.reject')}
                </Button>
              </div>
            </Card>
          )}

          <BroadSignRejectCreationModal
            creation={previewedCreation ?? undefined}
            isModalOpen={showRejectionModal}
            setModalOpen={setShowRejectionModal}
            onStatusChange={setPreviewedCreationStatus}
          />

          <BroadSignCreationPreviewModal
            creation={previewedCreation ?? undefined}
            isModalOpen={showPreviewModal}
            setModalOpen={setShowPreviewModal}
          />
        </div>

        <Card className='Broadsign__content'>
          {previewedCreation ? (
            // the tables carry the spinner themselves, reloading them must not take them off screen
            <BroadSignCreationMediaTable
              acceptances={acceptances}
              creationId={previewedCreation.id}
              loading={acceptancesLoading}
            />
          ) : (
            // the tables belong to a creation, until one is picked there is nothing for them to show
            <div className='Broadsign__empty'>
              <p>{t('broadSign.noCreationSelected')}</p>
            </div>
          )}
          <BroadSignCreationModal
            creation={selectedCreation}
            closeClick={closeCreationModal}
          />
        </Card>
      </div>
    </div>
  )
}

export default Broadsign
